import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Play } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { firebaseAuth } from '../../../../lib/firebase';
import {
  DEFAULT_CLIENT_FILTERS,
  INSPIRATION_FOOTER,
  INSPIRATION_INTRO,
  USAGE_CHIPS,
  effectiveTag,
  emptyCopy,
  filterCards,
  tabCounts,
  tagChips,
  type ClientFilters,
} from '../../../../lib/portalInspiration/clientView';
import { howToSteps, parseInspirationBold } from '../../../../lib/portalInspiration/entryRules';
import type { InspirationMediaType, PortalInspirationCard } from '../../../../types/portalInspiration';
import { fetchPortalInspiration } from '../portalApi';
import { PortalShell } from '../PortalShell';

// CLIENT PORTAL — INSPIRATION (Run I). VIEW ONLY: no upload, no edit, no download control.
//
// ⚠️ Nothing on this page decides who sees what. The server resolves the scope from the session,
// applies each entry's audience and returns signed media URLs for the entries this viewer may see;
// this page renders what it is given.
//
// ⚠️ Written in shadcn token classes (text-foreground, bg-card, border, text-muted-foreground), never
// slate-* literals: the portal declares those tokens per mode, so the page is right in light and dark.
// ⚠️ TEXT IS RENDERED AS TEXT. The `**payoff**` markup becomes <strong> through parseInspirationBold's
// segments - there is no innerHTML anywhere in this file, so a description cannot inject markup.
// ⚠️ No download button, and the right-click / drag affordances are switched off on the media. That is
// a courtesy, not protection: the media is served from signed CDN URLs the browser can always fetch.

export function PortalInspirationPage() {
  const query = useQuery({
    queryKey: ['portal-inspiration-cards'],
    queryFn: fetchPortalInspiration,
    retry: false,
  });
  const [filters, setFilters] = useState<ClientFilters>(DEFAULT_CLIENT_FILTERS);
  const cards = useMemo(() => query.data?.cards ?? [], [query.data]);
  const counts = tabCounts(cards);
  const tags = useMemo(() => tagChips(cards, filters.mediaType), [cards, filters.mediaType]);
  const tag = effectiveTag(filters.tag, tags);
  const shown = filterCards(cards, { ...filters, tag });
  const filtered = filters.usage !== 'all' || tag !== '';

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Inspiration</h1>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">{INSPIRATION_INTRO}</p>

      <div className="mt-6">
        {query.isPending ? (
          <CenteredSpinner />
        ) : query.isError ? (
          <div className="max-w-md">
            <p className="text-sm text-muted-foreground">We couldn’t load this section. Try again in a moment.</p>
            <Button variant="outline" className="mt-3" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : cards.length === 0 ? (
          <p className="max-w-prose text-sm text-muted-foreground">
            Nothing here yet. When the Kyck team adds ideas for your business, they will appear here.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Type">
              {(['video', 'image'] as const).map((t) => (
                <Chip key={t} pressed={filters.mediaType === t} onClick={() => setFilters({ ...filters, mediaType: t, tag: '' })}>
                  {t === 'video' ? 'Videos' : 'Images'} ({counts[t]})
                </Chip>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Use">
              {USAGE_CHIPS.map((u) => (
                <Chip key={u.id} pressed={filters.usage === u.id} onClick={() => setFilters({ ...filters, usage: u.id })}>
                  {u.label}
                </Chip>
              ))}
            </div>
            {tags.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Tags">
                <Chip pressed={tag === ''} onClick={() => setFilters({ ...filters, tag: '' })}>
                  All tags
                </Chip>
                {tags.map((t) => (
                  <Chip key={t} pressed={tag === t} onClick={() => setFilters({ ...filters, tag: t })}>
                    {t}
                  </Chip>
                ))}
              </div>
            )}

            {shown.length === 0 ? (
              <p className="mt-6 max-w-prose text-sm text-muted-foreground">{emptyCopy(filters.mediaType, filtered)}</p>
            ) : (
              <ul className="mt-6 grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((c) => (
                  <InspirationCard key={c.id} card={c} />
                ))}
              </ul>
            )}
            <p className="mt-8 text-xs font-medium uppercase tracking-wider text-muted-foreground">{INSPIRATION_FOOTER}</p>
          </>
        )}
      </div>
    </PortalShell>
  );
}

function Chip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
        (pressed ? 'border-foreground bg-foreground text-background' : 'border-border bg-card text-foreground hover:bg-muted')
      }
    >
      {children}
    </button>
  );
}

/** Text with the `**payoff**` segments in <strong>. React text nodes only. */
export function RichText({ text }: { text: string }) {
  return (
    <>
      {parseInspirationBold(text).map((s, i) => (s.bold ? <strong key={i} className="font-semibold text-foreground">{s.text}</strong> : <span key={i}>{s.text}</span>))}
    </>
  );
}

export function InspirationCard({ card }: { card: PortalInspirationCard }) {
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState(false);
  const steps = howToSteps(card.howTo);
  const noCtx = (e: React.SyntheticEvent) => e.preventDefault();

  return (
    <li className="overflow-hidden rounded-lg border border-border bg-card">
      <div className="relative bg-black">
        {card.mediaType === 'image' ? (
          card.imageUrl ? (
            <img src={card.imageUrl} alt={card.title} draggable={false} onContextMenu={noCtx} className="max-h-[32rem] w-full object-contain" loading="lazy" />
          ) : null
        ) : playing && card.videoUrl ? (
          <video
            src={card.videoUrl}
            poster={card.posterUrl ?? undefined}
            controls
            controlsList="nodownload noplaybackrate"
            disablePictureInPicture
            muted
            autoPlay
            playsInline
            onContextMenu={noCtx}
            className="max-h-[32rem] w-full"
          />
        ) : (
          <button type="button" onClick={() => setPlaying(true)} className="relative block w-full" aria-label={`Play ${card.title}`}>
            {card.posterUrl ? (
              <img src={card.posterUrl} alt="" draggable={false} onContextMenu={noCtx} className="max-h-[32rem] w-full object-contain" loading="lazy" />
            ) : (
              <div className="aspect-[9/16] max-h-[32rem] w-full bg-muted" />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/60 text-white">
                <Play className="h-6 w-6" />
              </span>
            </span>
          </button>
        )}
      </div>
      <div className="space-y-2 p-4">
        <h2 className={'text-base font-bold text-foreground ' + (card.mediaType === 'video' ? 'uppercase tracking-wide' : '')}>{card.title}</h2>
        {card.trendLine && (
          <p className="text-sm text-foreground">
            <span className="font-semibold">The trend: </span>
            {card.trendLine}
          </p>
        )}
        <p className="text-sm leading-relaxed text-muted-foreground">
          <RichText text={card.description} />
        </p>
        {card.suggestedLines.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-foreground">Try one of these lines</p>
            <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
              {card.suggestedLines.map((l, i) => (
                <li key={i}>“{l}”</li>
              ))}
            </ul>
          </div>
        )}
        {steps.length > 0 && (
          <div>
            <button
              type="button"
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="text-sm font-medium text-foreground underline underline-offset-4"
            >
              How to {open ? '▴' : '▾'}
            </button>
            {open && (
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                {steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export type { InspirationMediaType };
